import React, { memo, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import { getCountdown } from "../helper/Countdown";
import { FlashSalesServices } from "../services/FlashSalesServices";

const SampleNextArrow = memo(function SampleNextArrow(props) {
  const { className, style, onClick } = props;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${className} slick-next slick-arrow flex-center rounded-circle border border-gray-100 hover-border-main-600 text-xl hover-bg-main-600 hover-text-white transition-1`}
      style={{ ...style }}
      aria-label="Next"
    >
      <i className="ph ph-caret-right" />
    </button>
  );
});

const SamplePrevArrow = memo(function SamplePrevArrow(props) {
  const { className, style, onClick } = props;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${className} slick-prev slick-arrow flex-center rounded-circle border border-gray-100 hover-border-main-600 text-xl hover-bg-main-600 hover-text-white transition-1`}
      style={{ ...style }}
      aria-label="Previous"
    >
      <i className="ph ph-caret-left" />
    </button>
  );
});

const FlashSalesOne = () => {
  const [flashSales, setFlashSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await FlashSalesServices.getFlashSales(true);
        setFlashSales(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        console.error("Flash sales load failed:", e);
        setFlashSales([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const count = flashSales.length;
  const slidesToShow = Math.min(2, Math.max(1, count));
  const hasMultiple = count > 1;

  const settings = useMemo(
    () => ({
      dots: false,
      arrows: hasMultiple,
      infinite: hasMultiple && count > slidesToShow,
      speed: 900,
      slidesToShow,
      slidesToScroll: 1,
      autoplay: hasMultiple,
      autoplaySpeed: 4500,
      nextArrow: <SampleNextArrow />,
      prevArrow: <SamplePrevArrow />,
      centerMode: false,
      adaptiveHeight: true,
      responsive: [
        {
          breakpoint: 991,
          settings: {
            slidesToShow: 1,
            arrows: hasMultiple,
            infinite: hasMultiple && count > 1,
            autoplay: hasMultiple,
          },
        },
      ],
    }),
    [hasMultiple, count, slidesToShow]
  );

  const cardBackgrounds = useMemo(
    () => [
      "linear-gradient(135deg, #fff 0%, #f1f7ff 55%, #e7f0ff 100%)",
      "linear-gradient(135deg, #fff 0%, #fff3f1 55%, #ffe7e1 100%)",
      "linear-gradient(135deg, #fff 0%, #f2fff6 55%, #e5ffef 100%)",
      "linear-gradient(135deg, #fff 0%, #f7f3ff 55%, #efe6ff 100%)",
      "linear-gradient(135deg, #fff 0%, #fffbe7 55%, #fff4bf 100%)",
    ],
    []
  );

  const getProductImageUrl = (product) => {
    if (!product?.image) return null;
    const img = String(product.image);
    if (img.startsWith("http")) return img;
    return `https://api.ifjona.com/storage/${img}`;
  };

  const CountdownDisplay = ({ endAt }) => {
    const _ = tick;
    const t = getCountdown(endAt);

    return (
      <div className="countdown-wrap d-flex flex-wrap gap-8 mt-16">
        {[
          { label: "Days", value: t.days },
          { label: "Hours", value: t.hours },
          { label: "Min", value: t.minutes },
          { label: "Sec", value: t.seconds },
        ].map((x) => (
          <div key={x.label} className="countdown-box">
            <div className="countdown-val">{x.value}</div>
            <div className="countdown-lbl">{x.label}</div>
          </div>
        ))}
      </div>
    );
  };

  const ShopNowButton = ({ productId }) => (
    <Link
      to={productId ? `/product-details/${productId}` : "/shop"}
      className="btn btn-main d-inline-flex align-items-center rounded-pill gap-8 mt-24"
    >
      Shop Now
      <span className="icon text-xl d-flex">
        <i className="ph ph-arrow-right" />
      </span>
    </Link>
  );

  // ✅ safe early return AFTER all hooks
  if (!loading && count === 0) {
    return null;
  }

  return (
    <section className="flash-sales pt-80">
      <div className="container container-lg">
        <div className="section-heading mb-24 flash-header">
          <div className="flash-header-top">
            <div className="flash-title-row">
              <h3 className="text-heading mb-8">Flash Sales Today</h3>
              <Link to="/shop" className="view-all-deals-btn">
                View All Deals <i className="ph ph-arrow-right" />
              </Link>
            </div>
            <p className="text-gray-600">
              Limited time offers — grab before it ends!
            </p>
          </div>
        </div>

        {loading ? (
          <div className="py-5 text-center">
            <div className="spinner-border" role="status" />
            <p className="mt-2">Loading flash sales...</p>
          </div>
        ) : (
          <div className="flash-sales__slider arrow-style-two">
            <Slider {...settings}>
              {flashSales.map((sale, idx) => {
                const product = sale?.product || null;
                const title = product?.title || "Flash Sale";
                const thumb = getProductImageUrl(product);
                const bg = cardBackgrounds[idx % cardBackgrounds.length];
                const currencySymbol =
                  sale.location_prices?.[0]?.currency_symbol || "$";

                return (
                  <div
                    key={sale.id}
                    className={`px-8 ${count === 1 ? "single-slide" : ""}`}
                  >
                    <div
                      className="flash-card rounded-16 overflow-hidden position-relative"
                      style={{
                        background: bg,
                        border: "1px solid rgba(0,0,0,0.06)",
                        boxShadow: "0 10px 24px rgba(17,24,39,.08)",
                        padding: 20,
                      }}
                    >
                      <div className="flash-card-inner d-flex flex-wrap align-items-center justify-content-between gap-16 position-relative">
                        <div className="flash-thumb">
                          <img
                            src={
                              thumb ||
                              "https://via.placeholder.com/240x240?text=No+Image"
                            }
                            alt={title}
                            className="flash-thumb-img"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src =
                                "https://via.placeholder.com/240x240?text=No+Image";
                            }}
                          />
                        </div>

                        <div
                          className="flash-content"
                          style={{ flex: 1, minWidth: 0 }}
                        >
                          <div className="d-flex flex-wrap align-items-center justify-content-between gap-10">
                            <h6 className="flash-title" title={title}>
                              {title}
                            </h6>

                            <span className="flash-badge">
                              {sale.discount_type === "percent"
                                ? `${sale.discount_value}% OFF`
                                : `${currencySymbol}${sale.discount_value} OFF`}
                            </span>
                          </div>

                          {(sale.product?.flash_sale_price ??
                            sale.discounted_price) ? (
                            <div className="mt-10">
                              <div className="text-sm text-gray-600">
                                Flash Price
                              </div>
                              <div className="flash-price">
                                {currencySymbol}
                                {Number(
                                  sale.product?.flash_sale_price ??
                                    sale.discounted_price
                                ).toFixed(2)}
                              </div>
                              {sale.product?.current_price && (
                                <div
                                  style={{
                                    fontSize: 13,
                                    color: "#9ca3af",
                                    textDecoration: "line-through",
                                    marginTop: 2,
                                  }}
                                >
                                  Was {currencySymbol}
                                  {Number(
                                    sale.product.current_price
                                  ).toFixed(2)}
                                </div>
                              )}
                            </div>
                          ) : null}

                          <CountdownDisplay endAt={sale.end_at} />

                          <div className="mt-18">
                            <ShopNowButton productId={sale.product_id} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Slider>
          </div>
        )}
      </div>

      <style>{`
        .flash-header{
          position: relative;
          padding-right: 160px;
        }

        .flash-title-row{
          display: inline-flex;
          align-items: center;
          gap: 14px;
          flex-wrap: wrap;
        }

        .flash-main-title{
          font-size: 30px;
          font-weight: 900;
          line-height: 1.15;
          letter-spacing: -0.2px;
        }

        .view-all-deals-btn{
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 800;
          color: #374151;
          white-space: nowrap;
          position: relative;
          transition: color .2s ease;
          transform: translateY(1px);
        }
        .view-all-deals-btn::after{
          content: "";
          position: absolute;
          left: 0;
          bottom: -3px;
          width: 0%;
          height: 2px;
          background: #0d6efd;
          transition: width .25s ease;
        }
        .view-all-deals-btn:hover{ color:#0d6efd; }
        .view-all-deals-btn:hover::after{ width:100%; }
        .view-all-deals-btn i{ transition: transform .25s ease; }
        .view-all-deals-btn:hover i{ transform: translateX(4px); }

        .flash-thumb{
          width: 240px;
          height: 240px;
          border-radius: 16px;
          background: rgba(255,255,255,0.75);
          border: 1px solid rgba(0,0,0,0.06);
          display:flex;
          align-items:center;
          justify-content:center;
          overflow:hidden;
          flex-shrink: 0;
        }
        .flash-thumb-img{
          width: 92%;
          height: 92%;
          object-fit: contain;
          transform: scale(1.08);
        }
        .flash-title{
          margin: 0;
          font-size: 22px;
          font-weight: 800;
          line-height: 1.2;
          overflow:hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 100%;
        }
        .flash-badge{
          background: rgba(13,110,253,0.12);
          color: #0d6efd;
          padding: 8px 10px;
          border-radius: 999px;
          font-weight: 700;
          white-space: nowrap;
        }
        .flash-price{
          font-size: 20px;
          font-weight: 900;
          color: #0d6efd;
        }
        .countdown-box{
          min-width: 64px;
          padding: 10px 12px;
          border-radius: 12px;
          background: rgba(255,255,255,0.75);
          border: 1px solid rgba(0,0,0,0.06);
          backdrop-filter: blur(6px);
          text-align:center;
        }
        .countdown-val{ font-size: 16px; font-weight: 800; line-height: 1.1; }
        .countdown-lbl{ font-size: 12px; color: #6b7280; margin-top: 2px; }

        .single-slide{
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
        .single-slide .flash-card{
          max-width: 980px;
          margin: 0 auto;
        }

        @media (min-width: 992px) and (max-width: 1199px){
          .flash-header{ padding-right: 200px; }
          .flash-main-title{ font-size: 28px; }
          .flash-thumb{ width: 200px; height: 200px; }
          .flash-title{ font-size: 20px; }
        }

        @media (max-width: 991px){
          .flash-header{ padding-right: 0; }
          .flash-main-title{ font-size: 26px; }
          .flash-thumb{ width: 220px; height: 220px; }
          .flash-title{ white-space: normal; }
        }

        @media (max-width: 575px){
          .flash-main-title{ font-size: 24px; }
          .flash-thumb{ width: 100%; height: 220px; }
          .flash-thumb-img{ width: 80%; height: 80%; }
          .flash-content{ width: 100%; text-align: center; }
          .countdown-wrap{ justify-content: center; }
          .flash-title{ font-size: 20px; }
        }

        @media (max-width: 575px){
          .flash-card{
            padding: 14px !important;
          }

          .flash-main-title{
            font-size: 22px;
            line-height: 1.2;
          }

          .flash-title{
            font-size: 18px;
            line-height: 1.25;
            margin-bottom: 6px;
          }

          .flash-thumb{
            width: 100%;
            height: 180px;
            margin-bottom: 8px;
          }
          .flash-thumb-img{
            width: 75%;
            height: 75%;
          }

          .flash-content{
            width: 100%;
            text-align: center;
          }

          .flash-price{
            font-size: 18px;
            margin-top: 4px;
          }

          .flash-badge{
            padding: 6px 8px;
            font-size: 12px;
          }

          .countdown-wrap{
            justify-content: center;
            margin-top: 10px;
            gap: 6px;
          }
          .countdown-box{
            min-width: 52px;
            padding: 6px 8px;
          }
          .countdown-val{
            font-size: 14px;
          }
          .countdown-lbl{
            font-size: 11px;
          }

          .flash-card .btn{
            padding: 8px 14px;
            font-size: 13px;
          }

          .mt-24{ margin-top: 14px !important; }
          .mt-18{ margin-top: 10px !important; }
        }
      `}</style>
    </section>
  );
};

export default FlashSalesOne;